/**
 * `/tasks` — the staff Tasks board.
 *
 * A **Management** section (one card per admin in `staff_allowlist`) and a
 * **Staff** section (one card per active trainer). Each card lists that
 * person's open/recent tasks; tick the checkbox to complete; double-click a
 * row to edit; "+ Add task" creates a task on that card (title + category +
 * due date). All mutations go through the `create_task` / `update_task`
 * canonical actions and then refetch `GET /api/tasks-board`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  type TaskStatusSignal,
  type TasksBoardAssignee,
  type TasksBoardResponse,
  type TasksBoardTask,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { studioTimeZone } from "../lib/studio-tz";
import { formatTaskTiming } from "../lib/time-ago";

/** Status-dot labels (the hover tooltip). The dot COLOUR is CSS
 *  (`.task-status-dot.status-<signal>`); this is the human explanation. */
const STATUS_DOT_LABEL: Record<TaskStatusSignal, string> = {
  onboarding: "🔴 Onboarding incomplete — the charter isn't finished, so the worker won't start it",
  in_progress: "🔵 In progress — charter complete + recent work activity",
  done: "🟢 Done",
  ready: "⚪ Ready — onboarded, waiting to be picked up",
  cancelled: "⚪ Cancelled",
};

const CATEGORY_SUGGESTIONS = [
  "Admin",
  "Leads/Sales",
  "Client follow-up",
  "Studio/Facilities",
  "Marketing",
];

/** A canonical-action runner that refetches the board; resolves to an error
 * code string (or null on success). */
type RunMutation = (
  action: string,
  input: Record<string, unknown>,
) => Promise<string | null>;

function assigneeBodyKey(
  kind: "manager" | "trainer",
): "assigned_staff_id" | "assigned_trainer_id" {
  return kind === "manager" ? "assigned_staff_id" : "assigned_trainer_id";
}

/** Phase C — drag-to-reassign. A task row is dragged onto a card; the card's
 * identity is the reassignment target (or the General card = unassign). */
const TASK_DRAG_MIME = "application/x-layer-task-id";
type ReassignTarget =
  | { readonly kind: "manager" | "trainer"; readonly id: string }
  | { readonly kind: "unassigned" };
/** Dispatch the update_task patch for a drop onto a target card. */
type ReassignTask = (taskId: string, target: ReassignTarget) => void;

/** Card drop-target behaviour: highlight on a task drag-over, fire on drop. */
function useTaskDrop(
  onDropTask: (taskId: string) => void,
  disabled: boolean,
): {
  over: boolean;
  handlers: {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: React.DragEvent) => void;
  };
} {
  const [over, setOver] = useState<boolean>(false);
  return {
    over,
    handlers: {
      onDragOver: (e: React.DragEvent): void => {
        if (disabled) return;
        if (!Array.from(e.dataTransfer.types).includes(TASK_DRAG_MIME)) return;
        e.preventDefault(); // allow the drop
        setOver(true);
      },
      onDragLeave: (): void => setOver(false),
      onDrop: (e: React.DragEvent): void => {
        e.preventDefault();
        setOver(false);
        const id = e.dataTransfer.getData(TASK_DRAG_MIME);
        if (id.length > 0) onDropTask(id);
      },
    },
  };
}

/** Today's date in the STUDIO's calendar as YYYY-MM-DD (en-CA formats so) —
 * "overdue"/"due today" must not flip with the viewer's browser zone. */
function todayStudioISODate(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: studioTimeZone(),
  }).format(new Date());
}

interface DueMeta {
  readonly label: string;
  readonly overdue: boolean;
  readonly dueToday: boolean;
}
function dueMeta(dueAt: string | null): DueMeta | null {
  if (dueAt === null) return null;
  const dateStr = dueAt.slice(0, 10); // YYYY-MM-DD as stored (noon UTC)
  const today = todayStudioISODate();
  let label = dateStr;
  const d = new Date(dueAt);
  if (!Number.isNaN(d.getTime())) {
    label = d.toLocaleDateString("en-GB", {
      timeZone: studioTimeZone(),
      day: "2-digit",
      month: "short",
    });
  }
  return { label, overdue: dateStr < today, dueToday: dateStr === today };
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function Tasks(): JSX.Element {
  const [data, setData] = useState<TasksBoardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [editing, setEditing] = useState<TasksBoardTask | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    const r = await api.tasksBoard();
    setData(r);
    setError(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .tasksBoard()
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? (e.code ?? `http_${e.status}`) : e.message,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runMutation = useCallback<RunMutation>(
    async (action, input) => {
      setBusy(true);
      try {
        const res = await dispatchAction<Record<string, unknown>, unknown>(
          action,
          input,
        );
        if (!res.ok) return res.code;
        await reload();
        return null;
      } catch (e) {
        return (e as Error).message;
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  // Phase C — drag a task onto a card to reassign it (or the General card to
  // unassign). One update_task patch per drop.
  const reassignTask = useCallback<ReassignTask>(
    (taskId, target) => {
      const patch: Record<string, unknown> =
        target.kind === "manager"
          ? { assigned_staff_id: target.id }
          : target.kind === "trainer"
            ? { assigned_trainer_id: target.id }
            : { unassign: true };
      void runMutation("update_task", { task_id: taskId, patch });
    },
    [runMutation],
  );

  if (loading) return <p className="empty">Loading tasks…</p>;
  if (error !== null) {
    return <p className="empty">Couldn&apos;t load tasks ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;

  return (
    <div>
      <h1 className="page-title">Tasks</h1>
      {/* One card, so the full width — under `tasks-grid-2` it sat in the left
          half with the right half empty, which read as a missing second card
          rather than as a deliberate single one. */}
      <h2 className="tasks-section-title">General · unassigned</h2>
      <div className="tasks-grid tasks-grid-1">
        <UnassignedCard
          tasks={data.unassigned}
          busy={busy}
          onMutate={runMutation}
          onEdit={setEditing}
          onReassign={reassignTask}
        />
      </div>

      <h2 className="tasks-section-title">Management</h2>
      {data.managers.length === 0 ? (
        <p className="empty">No managers configured yet.</p>
      ) : (
        <div className="tasks-grid tasks-grid-2">
          {data.managers.map((a) => (
            <AssigneeCard
              key={`m-${a.id}`}
              assignee={a}
              busy={busy}
              onMutate={runMutation}
              onEdit={setEditing}
              onReassign={reassignTask}
            />
          ))}
        </div>
      )}

      <h2 className="tasks-section-title">Staff</h2>
      {data.trainers.length === 0 ? (
        <p className="empty">No active trainers.</p>
      ) : (
        <div className="tasks-grid">
          {data.trainers.map((a) => (
            <AssigneeCard
              key={`t-${a.id}`}
              assignee={a}
              busy={busy}
              onMutate={runMutation}
              onEdit={setEditing}
              onReassign={reassignTask}
            />
          ))}
        </div>
      )}

      {/* Shared category suggestions for every add/edit input. */}
      <datalist id="task-categories">
        {CATEGORY_SUGGESTIONS.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {editing !== null ? (
        <EditTaskModal
          task={editing}
          busy={busy}
          onMutate={runMutation}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-person card.
// ---------------------------------------------------------------------------

function AssigneeCard({
  assignee,
  busy,
  onMutate,
  onEdit,
  onReassign,
}: {
  readonly assignee: TasksBoardAssignee;
  readonly busy: boolean;
  readonly onMutate: RunMutation;
  readonly onEdit: (task: TasksBoardTask) => void;
  readonly onReassign: ReassignTask;
}): JSX.Element {
  const bodyKey = assigneeBodyKey(assignee.kind);
  const openCount = assignee.tasks.filter((t) => t.status !== "done").length;
  const fallback = assignee.kind === "manager" ? "Manager" : "Trainer";
  const drop = useTaskDrop(
    (taskId) => onReassign(taskId, { kind: assignee.kind, id: assignee.id }),
    busy,
  );
  return (
    <section
      className={`panel tasks-card${drop.over ? " tasks-card-dragover" : ""}`}
      {...drop.handlers}
    >
      <h3 className="tasks-card-title">
        {assignee.display_name ?? fallback}
        <span className="dc-muted"> · {openCount} open</span>
      </h3>
      {assignee.tasks.length === 0 ? (
        <p className="empty tasks-empty">No tasks.</p>
      ) : (
        <ul className="tasks-list">
          {assignee.tasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              busy={busy}
              onMutate={onMutate}
              onEdit={() => onEdit(t)}
            />
          ))}
        </ul>
      )}
      <AddTaskForm
        bodyKey={bodyKey}
        assigneeId={assignee.id}
        busy={busy}
        onMutate={onMutate}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// General / unassigned card (Phase C) — the triage queue. WhatsApp requests +
// the chat trouble-fallback (G2) land here; staff drag a task onto a person's
// card to assign it, or back here to unassign.
// ---------------------------------------------------------------------------

function UnassignedCard({
  tasks,
  busy,
  onMutate,
  onEdit,
  onReassign,
}: {
  readonly tasks: ReadonlyArray<TasksBoardTask>;
  readonly busy: boolean;
  readonly onMutate: RunMutation;
  readonly onEdit: (task: TasksBoardTask) => void;
  readonly onReassign: ReassignTask;
}): JSX.Element {
  const openCount = tasks.filter((t) => t.status !== "done").length;
  const drop = useTaskDrop(
    (taskId) => onReassign(taskId, { kind: "unassigned" }),
    busy,
  );
  return (
    <section
      className={`panel tasks-card tasks-card-general${drop.over ? " tasks-card-dragover" : ""}`}
      {...drop.handlers}
    >
      <h3 className="tasks-card-title">
        General
        <span className="dc-muted"> · {openCount} open · drag to assign</span>
      </h3>
      {tasks.length === 0 ? (
        <p className="empty tasks-empty">
          No unassigned tasks. Incoming WhatsApp requests land here.
        </p>
      ) : (
        <ul className="tasks-list">
          {tasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              busy={busy}
              onMutate={onMutate}
              onEdit={() => onEdit(t)}
            />
          ))}
        </ul>
      )}
      <AddTaskForm busy={busy} onMutate={onMutate} />
    </section>
  );
}

function TaskRow({
  task,
  busy,
  onMutate,
  onEdit,
}: {
  readonly task: TasksBoardTask;
  readonly busy: boolean;
  readonly onMutate: RunMutation;
  readonly onEdit: () => void;
}): JSX.Element {
  const due = dueMeta(task.due_at);
  const done = task.status === "done";
  // Work-time + quick summary (2026-07-14): active tasks show real effort time
  // from pa_task_context; done tasks stay historical (no meta line).
  const timing = done ? "" : formatTaskTiming(task, Date.now());
  const showMeta = !done && (timing.length > 0 || task.work_summary !== null);
  return (
    <li
      className={`task-row${done ? " task-done" : ""}`}
      draggable={!busy}
      onDragStart={(e) => {
        e.dataTransfer.setData(TASK_DRAG_MIME, task.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDoubleClick={onEdit}
      title="Drag to a card to reassign · double-click to edit"
    >
      <div className="task-row-head">
        <input
          type="checkbox"
          className="task-check"
          checked={done}
          disabled={busy}
          aria-label={done ? "Mark not done" : "Mark done"}
          onChange={(e) => {
            void onMutate("update_task", {
              task_id: task.id,
              patch: { status: e.target.checked ? "done" : "pending" },
            });
          }}
        />
        <span
          className={`task-status-dot status-${task.status_signal}`}
          title={STATUS_DOT_LABEL[task.status_signal]}
          aria-label={STATUS_DOT_LABEL[task.status_signal]}
        />
        <span className="task-title">{task.title}</span>
        {task.status === "onboarding" ? (
          <span
            className="tag task-onboarding"
            title="Being onboarded — created early; the deep worker hasn't started it yet"
          >
            Onboarding…
          </span>
        ) : null}
        {task.pinned_at !== null ? (
          <span
            className="tag task-pinned"
            title="Pinned — the operator's top priority; the deep worker finishes this before other tasks"
          >
            📌 Pinned
          </span>
        ) : null}
        {task.group_ref !== null ? (
          <span
            className="tag task-shared"
            title={`Shared task — co-owned in the ${task.group_ref} group`}
          >
            🤝 Shared
          </span>
        ) : null}
        {task.ref !== null ? (
          <span className="tag" title="@Task handle">
            @{task.ref}
          </span>
        ) : null}
        {task.category !== null && task.category.length > 0 ? (
          <span className="tag">{task.category}</span>
        ) : null}
        {due !== null ? (
          <span
            className={`task-due${
              due.overdue
                ? " task-overdue"
                : due.dueToday
                  ? " task-due-today"
                  : ""
            }`}
          >
            {due.overdue ? "‼️ " : due.dueToday ? "📅 " : ""}
            {due.label}
          </span>
        ) : null}
        {/* Open the single-pane tracker. stopPropagation so it doesn't trigger
            the row's drag-to-reassign / double-click-to-edit. */}
        <Link
          to={`/tasks/${encodeURIComponent(task.id)}`}
          className="task-open"
          draggable={false}
          title="Open task pane"
          onClick={(e) => e.stopPropagation()}
          style={{ marginLeft: "auto", textDecoration: "none" }}
        >
          ↗
        </Link>
      </div>
      {showMeta ? (
        <div className="task-meta">
          <span className="task-meta-time">{timing}</span>
          {task.work_summary !== null ? (
            <span className="task-meta-summary" title={task.work_summary}>
              {task.work_summary}
            </span>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Add-task inline form (assignee = the card's person).
// ---------------------------------------------------------------------------

function AddTaskForm({
  bodyKey,
  assigneeId,
  busy,
  onMutate,
}: {
  // Omit both to add an UNASSIGNED / general task (the General card).
  readonly bodyKey?: "assigned_staff_id" | "assigned_trainer_id";
  readonly assigneeId?: string;
  readonly busy: boolean;
  readonly onMutate: RunMutation;
}): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);

  if (!open) {
    return (
      <button
        type="button"
        className="task-add-btn"
        onClick={() => setOpen(true)}
      >
        + Add task
      </button>
    );
  }

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const t = title.trim();
    if (t.length === 0) return;
    const input: Record<string, unknown> = { title: t };
    if (bodyKey !== undefined && assigneeId !== undefined) {
      input[bodyKey] = assigneeId;
    }
    if (category.trim().length > 0) input["category"] = category.trim();
    if (date.length > 0) input["due_at"] = `${date}T12:00:00Z`;
    const code = await onMutate("create_task", input);
    if (code !== null) {
      setErr(code);
      return;
    }
    setTitle("");
    setCategory("");
    setDate("");
    setErr(null);
    setOpen(false);
  };

  return (
    <form className="task-add-form" onSubmit={(e) => void submit(e)}>
      <input
        type="text"
        placeholder="Task title"
        value={title}
        maxLength={200}
        autoFocus
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="task-add-row">
        <input
          type="text"
          placeholder="Category"
          list="task-categories"
          value={category}
          maxLength={60}
          onChange={(e) => setCategory(e.target.value)}
        />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>
      <div className="task-add-actions">
        <button type="submit" disabled={busy || title.trim().length === 0}>
          Add
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setErr(null);
          }}
        >
          Cancel
        </button>
        {err !== null ? (
          <span className="tag tag-error">{err}</span>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Double-click edit modal.
// ---------------------------------------------------------------------------

function EditTaskModal({
  task,
  busy,
  onMutate,
  onClose,
}: {
  readonly task: TasksBoardTask;
  readonly busy: boolean;
  readonly onMutate: RunMutation;
  readonly onClose: () => void;
}): JSX.Element {
  const [title, setTitle] = useState<string>(task.title);
  const [category, setCategory] = useState<string>(task.category ?? "");
  const [date, setDate] = useState<string>(
    task.due_at !== null ? task.due_at.slice(0, 10) : "",
  );
  const [description, setDescription] = useState<string>(
    task.description ?? "",
  );
  const [err, setErr] = useState<string | null>(null);

  const isDirty =
    title.trim() !== task.title ||
    category.trim() !== (task.category ?? "") ||
    date !== (task.due_at !== null ? task.due_at.slice(0, 10) : "") ||
    description.trim() !== (task.description ?? "");

  const confirmClose = useCallback((): void => {
    if (
      isDirty &&
      !window.confirm(
        "You have unsaved changes to this task. Close without saving?",
      )
    ) {
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  // Guard a browser window/tab close (or refresh) while edits are unsaved —
  // the modal was silently discarding typed details on an accidental close.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Auto-grow the Details textarea to fit its content so the modal itself
  // grows with the text — no manual drag-to-resize (and no inner scrollbar).
  const descRef = useRef<HTMLTextAreaElement>(null);
  const growDesc = useCallback((el: HTMLTextAreaElement): void => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => {
    const el = descRef.current;
    if (el !== null) growDesc(el);
  }, [growDesc]);

  // True only when the pointer was PRESSED on the backdrop itself — so a
  // drag that starts inside the modal (selecting text) and releases out on
  // the backdrop does NOT count as an outside click and must not close.
  const pressedOnBackdrop = useRef(false);

  const save = async (): Promise<void> => {
    const patch: Record<string, unknown> = {};
    if (title.trim() !== task.title && title.trim().length > 0) {
      patch["title"] = title.trim();
    }
    const cat = category.trim();
    if (cat !== (task.category ?? "")) {
      patch["category"] = cat.length > 0 ? cat : null;
    }
    const origDate = task.due_at !== null ? task.due_at.slice(0, 10) : "";
    if (date !== origDate) {
      patch["due_at"] = date.length > 0 ? `${date}T12:00:00Z` : null;
    }
    const desc = description.trim();
    if (desc !== (task.description ?? "")) {
      patch["description"] = desc.length > 0 ? desc : null;
    }
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    const code = await onMutate("update_task", { task_id: task.id, patch });
    if (code !== null) {
      setErr(code);
      return;
    }
    onClose();
  };

  const deleteTask = async (): Promise<void> => {
    const code = await onMutate("update_task", {
      task_id: task.id,
      patch: { status: "cancelled" },
    });
    if (code !== null) {
      setErr(code);
      return;
    }
    onClose();
  };

  return (
    <div
      className="row-edit-modal-backdrop"
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Close ONLY on a genuine outside click — pressed AND released on
        // the backdrop. A drag from inside the modal that releases out
        // here (e.g. selecting/dragging text) must not close.
        if (e.target === e.currentTarget && pressedOnBackdrop.current) {
          confirmClose();
        }
        pressedOnBackdrop.current = false;
      }}
      role="presentation"
    >
      <div
        className="row-edit-modal task-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-edit-title"
      >
        <h2 id="task-edit-title" className="row-edit-modal-title">
          Edit task
        </h2>
        <div className="row-edit-modal-form">
          <label className="row-edit-modal-label" htmlFor="task-edit-title-input">
            <span>Title</span>
            <input
              id="task-edit-title-input"
              type="text"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="row-edit-modal-label" htmlFor="task-edit-category">
            <span>Category</span>
            <input
              id="task-edit-category"
              type="text"
              list="task-categories"
              maxLength={60}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </label>
          <label className="row-edit-modal-label" htmlFor="task-edit-due">
            <span>Due date</span>
            <input
              id="task-edit-due"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className="row-edit-modal-label" htmlFor="task-edit-desc">
            <span>Details</span>
            <textarea
              id="task-edit-desc"
              ref={descRef}
              className="task-edit-textarea"
              maxLength={20000}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                growDesc(e.currentTarget);
              }}
            />
          </label>
        </div>
        {err !== null ? (
          <div className="row-edit-modal-error">
            <strong>Error:</strong>{" "}
            <code className="tag tag-error">{err}</code>
          </div>
        ) : null}
        <div className="row-edit-modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={confirmClose}
            disabled={busy}
          >
            Close
          </button>
          <button
            type="button"
            className="task-delete-btn"
            onClick={() => void deleteTask()}
            disabled={busy}
          >
            Delete task
          </button>
          <button type="button" onClick={() => void save()} disabled={busy}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
